import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useOrgPermissions } from '@/hooks/useOrgPermissions'
import { useToast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Trophy, Star, Globe, Search, Sparkles, Radio, Plus, Calendar, Check, Trash2, Award, Loader2
} from 'lucide-react'

const ICON_MAP: Record<string, React.ReactNode> = {
  trophy:   <Trophy className="w-5 h-5" />,
  star:     <Star className="w-5 h-5" />,
  globe:    <Globe className="w-5 h-5" />,
  search:   <Search className="w-5 h-5" />,
  radio:    <Radio className="w-5 h-5" />,
  sparkles: <Sparkles className="w-5 h-5" />,
}

function formatDate(iso: string | null): string {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
}

export function AwardsView() {
  const { orgId, can } = useOrgPermissions()
  const { toast } = useToast()
  const qc = useQueryClient()

  // --- Nomination state ---
  const [nominateOpen, setNominateOpen] = useState(false)
  const [nominateCategoryId, setNominateCategoryId] = useState<string | null>(null)
  const [nominateJournalistId, setNominateJournalistId] = useState<string>('')
  const [journalistPickerOpen, setJournalistPickerOpen] = useState(false)
  const [justification, setJustification] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // --- Past winners / cycle state ---
  const [pastOpen, setPastOpen] = useState(false)
  const [cycleOpen, setCycleOpen] = useState(false)
  const [cycleForm, setCycleForm] = useState({ name: '', year: new Date().getFullYear(), starts_at: '', ends_at: '' })
  const [savingCycle, setSavingCycle] = useState(false)

  // --- Category CRUD state ---
  const [catOpen, setCatOpen] = useState(false)
  const [catForm, setCatForm] = useState({ name: '', description: '', icon_name: 'trophy', points: 10, prize_description: '' })
  const [savingCat, setSavingCat] = useState(false)
  const [deletingCatId, setDeletingCatId] = useState<string | null>(null)

  // --- Declare winner state ---
  const [winnerNom, setWinnerNom] = useState<any>(null)
  const [declaringWinner, setDeclaringWinner] = useState(false)

  // --- Queries ---
  const { data: categories } = useQuery({
    queryKey: ['award-categories', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await supabase
        .from('award_categories')
        .select('*')
        .eq('org_id', orgId!)
        .order('display_order')
      return data || []
    }
  })

  const { data: activeCycle } = useQuery({
    queryKey: ['active-award-cycle', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await supabase
        .from('award_cycles')
        .select('*')
        .eq('org_id', orgId!)
        .eq('status', 'open')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      return data
    }
  })

  const { data: nominations } = useQuery({
    queryKey: ['award-nominations', activeCycle?.id],
    enabled: !!activeCycle?.id,
    queryFn: async () => {
      const { data } = await supabase
        .from('award_nominations')
        .select(`
          id, category_id, justification,
          journalists(id, first_name, last_name,
            journalist_affiliations(is_primary, media_outlets(name)))
        `)
        .eq('cycle_id', activeCycle!.id)
      return data || []
    }
  })

  const { data: cycleWinners } = useQuery({
    queryKey: ['cycle-winners', activeCycle?.id],
    enabled: !!activeCycle?.id,
    queryFn: async () => {
      const { data } = await supabase
        .from('award_winners')
        .select('category_id, journalist_id')
        .eq('cycle_id', activeCycle!.id)
      return (data || []) as { category_id: string; journalist_id: string }[]
    }
  })

  const { data: journalists } = useQuery({
    queryKey: ['award-journalists', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await supabase
        .from('journalists')
        .select(`
          id, first_name, last_name, seniority,
          journalist_affiliations(is_primary, media_outlets(name))
        `)
        .eq('org_id', orgId!)
        .eq('status', 'active')
        .order('last_name')
      return (data || []) as any[]
    }
  })

  const { data: winners } = useQuery({
    queryKey: ['award-winners', orgId],
    enabled: !!orgId && pastOpen,
    queryFn: async () => {
      const { data } = await supabase
        .from('award_winners')
        .select(`
          id, declared_at,
          award_cycles(name, year),
          award_categories(name),
          journalists(first_name, last_name, journalist_affiliations(is_primary, media_outlets(name)))
        `)
        .eq('org_id', orgId!)
        .order('declared_at', { ascending: false })
      return data || []
    }
  })

  const { data: org } = useQuery({
    queryKey: ['org-name', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await supabase.from('organizations').select('name').eq('id', orgId!).single()
      return data
    }
  })

  if (!can('awards', 'view')) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">
        You do not have access to the Awards module.
      </div>
    )
  }

  const canNominate = can('awards', 'view')
  const canEdit = can('awards', 'edit')

  const openNominateFor = (categoryId: string) => {
    setNominateCategoryId(categoryId)
    setNominateJournalistId('')
    setJustification('')
    const cat = (categories || []).find((c: any) => c.id === categoryId)
    if (cat && journalists?.length) {
      if (cat.criteria === 'seniority') {
        const rising = journalists.find((j: any) => j.seniority === 'junior' || j.seniority === 'mid')
        if (rising) setNominateJournalistId(rising.id)
      } else {
        if (journalists[0]) setNominateJournalistId(journalists[0].id)
      }
    }
    setNominateOpen(true)
  }

  const submitNomination = async () => {
    if (!nominateJournalistId || !nominateCategoryId || !activeCycle?.id || !orgId) return
    if (justification.trim().length < 50) {
      toast({ title: 'Justification must be at least 50 characters.', variant: 'destructive' })
      return
    }
    setSubmitting(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      const { error } = await supabase
        .from('award_nominations')
        .insert({
          cycle_id:      activeCycle.id,
          category_id:   nominateCategoryId,
          journalist_id: nominateJournalistId,
          org_id:        orgId,
          nominated_by:  user?.id,
          justification: justification.trim(),
        })
      if (error) throw error
      toast({ title: 'Nomination submitted.' })
      setNominateOpen(false)
      qc.invalidateQueries({ queryKey: ['award-nominations', activeCycle.id] })
    } catch (e: any) {
      const msg = e?.message?.includes('unique')
        ? 'This journalist is already nominated in this category.'
        : 'Failed to submit nomination.'
      toast({ title: msg, variant: 'destructive' })
    } finally {
      setSubmitting(false)
    }
  }

  const createCycle = async () => {
    if (!cycleForm.name || !cycleForm.starts_at || !cycleForm.ends_at || !orgId) return
    setSavingCycle(true)
    try {
      const { error } = await supabase
        .from('award_cycles')
        .insert({
          org_id:    orgId,
          name:      cycleForm.name,
          year:      cycleForm.year,
          starts_at: cycleForm.starts_at,
          ends_at:   cycleForm.ends_at,
          status:    'open',
        })
      if (error) throw error
      toast({ title: 'Award cycle created.' })
      setCycleOpen(false)
      qc.invalidateQueries({ queryKey: ['active-award-cycle', orgId] })
    } catch {
      toast({ title: 'Failed to create cycle.', variant: 'destructive' })
    } finally {
      setSavingCycle(false)
    }
  }

  const createCategory = async () => {
    if (!catForm.name || !orgId) return
    setSavingCat(true)
    try {
      const { error } = await supabase
        .from('award_categories')
        .insert({
          org_id:            orgId,
          name:              catForm.name,
          description:       catForm.description,
          icon_name:         catForm.icon_name,
          points:            catForm.points,
          prize_description: catForm.prize_description || null,
          display_order:     (categories || []).length,
        })
      if (error) throw error
      toast({ title: 'Category created.' })
      setCatOpen(false)
      setCatForm({ name: '', description: '', icon_name: 'trophy', points: 10, prize_description: '' })
      qc.invalidateQueries({ queryKey: ['award-categories', orgId] })
    } catch {
      toast({ title: 'Failed to create category.', variant: 'destructive' })
    } finally {
      setSavingCat(false)
    }
  }

  const deleteCategory = async (catId: string) => {
    setDeletingCatId(catId)
    try {
      const { error } = await supabase.from('award_categories').delete().eq('id', catId)
      if (error) throw error
      toast({ title: 'Category deleted.' })
      qc.invalidateQueries({ queryKey: ['award-categories', orgId] })
    } catch {
      toast({ title: 'Failed to delete category.', variant: 'destructive' })
    } finally {
      setDeletingCatId(null)
    }
  }

  const declareWinner = async () => {
    if (!winnerNom || !orgId || !activeCycle?.id) return
    setDeclaringWinner(true)
    try {
      const { error } = await supabase
        .from('award_winners')
        .insert({
          journalist_id: winnerNom.journalists?.id,
          category_id:   winnerNom.category_id,
          cycle_id:      activeCycle.id,
          org_id:        orgId,
          declared_at:   new Date().toISOString(),
        })
      if (error) throw error
      toast({ title: 'Winner declared.' })
      setWinnerNom(null)
      qc.invalidateQueries({ queryKey: ['award-nominations', activeCycle.id] })
      qc.invalidateQueries({ queryKey: ['cycle-winners', activeCycle.id] })
      qc.invalidateQueries({ queryKey: ['award-winners', orgId] })
    } catch (e: any) {
      toast({ title: 'Failed to declare winner.', description: e.message, variant: 'destructive' })
    } finally {
      setDeclaringWinner(false)
    }
  }

  const selectedJournalist = journalists?.find((j: any) => j.id === nominateJournalistId)
  const selectedCategory = (categories || []).find((c: any) => c.id === nominateCategoryId)
  const winnerCategoryMap = new Set((cycleWinners || []).map(w => w.category_id))

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Awards</h1>
        <p className="text-justify text-gray-500 text-sm mt-1">
          Manage parliamentary press awards and journalist nominations.
        </p>
      </div>

      {/* Hero banner, active cycle */}
      {activeCycle ? (
        <div className="rounded-xl p-5" style={{ background: 'var(--color-header, #0D2240)' }}>
          <div className="text-[11px] font-medium uppercase tracking-widest mb-1" style={{ color: '#C8A84B' }}>
            {org?.name} Parliamentary Press Awards
          </div>
          <div className="text-lg font-medium text-white mb-1">
            {activeCycle.name}: Nominations Open
          </div>
          <p className="text-sm text-white/60 text-justify max-w-xl leading-relaxed">
            Nominations close {formatDate(activeCycle.ends_at)}. Recognising journalists who have
            demonstrated excellence in parliamentary reporting and contributed significantly to
            public understanding of {org?.name}.
          </p>
          <div className="flex gap-2 mt-4 flex-wrap">
            {canNominate && (
              <Button
                onClick={() => openNominateFor((categories || [])[0]?.id ?? '')}
                className="bg-[#C8A84B] hover:bg-[#B8983B] text-[#0D2240] font-semibold text-sm"
              >
                Open Nominations
              </Button>
            )}
            <Button
              variant="outline"
              className="text-white border-white/30 hover:bg-white/10 hover:text-white text-sm"
              onClick={() => setPastOpen(true)}
            >
              View Past Winners
            </Button>
          </div>
        </div>
      ) : canEdit ? (
        <div className="rounded-xl border border-dashed border-[#C8A84B]/40 p-6 text-center space-y-3">
          <Trophy className="w-8 h-8 text-[#C8A84B] mx-auto opacity-60" />
          <p className="text-justify text-sm text-muted-foreground">
            No active award cycle. Create one to open nominations.
          </p>
          <Button variant="outline" size="sm" className="gap-1" onClick={() => setCycleOpen(true)}>
            <Plus className="w-3.5 h-3.5" />
            Create Award Cycle
          </Button>
        </div>
      ) : null}

      {/* Category header */}
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-700">Award Categories</h2>
        {canEdit && (
          <Button size="sm" onClick={() => setCatOpen(true)} className="gap-1.5 bg-[#C8A84B] hover:bg-[#B8983B] text-white">
            <Plus className="w-3.5 h-3.5" />
            Add Category
          </Button>
        )}
      </div>

      {/* Category cards */}
      {(categories || []).length === 0 ? (
        <p className="text-justify text-sm text-muted-foreground">No award categories configured for this organisation.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(categories || []).map((cat: any) => {
            const catNominations = (nominations || []).filter((n: any) => n.category_id === cat.id)
            const hasWinner = winnerCategoryMap.has(cat.id)
            return (
              <div key={cat.id} className="bg-white border border-[#DDD8CE] rounded-xl p-4 flex flex-col">
                <div className="flex items-start justify-between mb-2">
                  <div className="text-[#C8A84B]">
                    {ICON_MAP[cat.icon_name] ?? <Trophy className="w-5 h-5" />}
                  </div>
                  {canEdit && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          type="button"
                          disabled={deletingCatId === cat.id}
                          className="text-muted-foreground hover:text-destructive transition-colors p-0.5"
                        >
                          {deletingCatId === cat.id
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : <Trash2 className="w-3.5 h-3.5" />}
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete "{cat.name}"?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This removes the category permanently. Any existing nominations in this category will lose their category reference. This cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => deleteCategory(cat.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Delete category
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
                <div className="font-semibold text-[13px] mb-1">{cat.name}</div>
                <p className="text-[12px] text-muted-foreground text-justify leading-relaxed mb-3 flex-1">
                  {cat.description}
                </p>
                <div className="text-[11px] font-medium" style={{ color: '#C8A84B' }}>
                  {cat.points} Coverage Points
                  {cat.prize_description && ` · ${cat.prize_description}`}
                </div>

                {activeCycle && (
                  <div className="mt-3 border-t border-[#DDD8CE] pt-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[11px] text-muted-foreground uppercase tracking-wide">
                        Nominations ({catNominations.length})
                      </div>
                      {hasWinner && (
                        <Badge className="text-[10px] bg-amber-100 text-amber-700 gap-0.5">
                          <Award className="w-2.5 h-2.5" />
                          Winner declared
                        </Badge>
                      )}
                    </div>
                    {catNominations.length === 0 ? (
                      <div className="text-[12px] text-muted-foreground">No nominations yet.</div>
                    ) : catNominations.map((nom: any) => (
                      <div key={nom.id} className="flex items-center justify-between py-1 gap-2">
                        <div className="text-[12px] font-medium truncate flex-1">
                          {nom.journalists?.first_name} {nom.journalists?.last_name}
                        </div>
                        {canEdit && !hasWinner && (
                          <button
                            type="button"
                            onClick={() => setWinnerNom(nom)}
                            className="text-[10px] text-[#C8A84B] hover:underline shrink-0 flex items-center gap-0.5"
                          >
                            <Award className="w-3 h-3" />
                            Declare winner
                          </button>
                        )}
                      </div>
                    ))}
                    {canNominate && !hasWinner && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="mt-2 w-full text-[12px] text-[#C8A84B] hover:text-[#C8A84B] hover:bg-[#C8A84B]/10"
                        onClick={() => openNominateFor(cat.id)}
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Nominate
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* ── Nominate sheet ── */}
      <Sheet open={nominateOpen} onOpenChange={setNominateOpen}>
        <SheetContent side="right" className="w-[480px]">
          <SheetHeader>
            <SheetTitle>Nominate a Journalist</SheetTitle>
            {selectedCategory && (
              <p className="text-justify text-sm text-muted-foreground mt-1">
                Category: <span className="font-medium text-foreground">{selectedCategory.name}</span>
              </p>
            )}
          </SheetHeader>
          <div className="space-y-5 mt-5">
            <div className="space-y-2">
              <Label>Journalist</Label>
              {nominateJournalistId && selectedJournalist && (
                <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg mb-2">
                  <Check className="w-4 h-4 text-green-600 shrink-0" />
                  <span className="text-sm font-medium">
                    {selectedJournalist.first_name} {selectedJournalist.last_name}
                  </span>
                  {selectedCategory?.criteria === 'seniority' && (
                    <Badge className="text-[10px] bg-blue-100 text-blue-700 ml-auto">AI Suggested</Badge>
                  )}
                </div>
              )}
              <Popover open={journalistPickerOpen} onOpenChange={setJournalistPickerOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="w-full justify-start text-left font-normal">
                    {nominateJournalistId && selectedJournalist
                      ? `${selectedJournalist.first_name} ${selectedJournalist.last_name}`
                      : 'Select journalist…'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-0">
                  <Command>
                    <CommandInput placeholder="Search journalists…" />
                    <CommandEmpty>No journalist found.</CommandEmpty>
                    <CommandGroup className="max-h-64 overflow-y-auto">
                      {(journalists || []).map((j: any) => {
                        const outlet = j.journalist_affiliations?.find((a: any) => a.is_primary)?.media_outlets?.name
                        return (
                          <CommandItem
                            key={j.id}
                            value={`${j.first_name} ${j.last_name}`}
                            onSelect={() => { setNominateJournalistId(j.id); setJournalistPickerOpen(false) }}
                          >
                            <div>
                              <div className="text-sm font-medium">{j.first_name} {j.last_name}</div>
                              {outlet && <div className="text-xs text-muted-foreground">{outlet}</div>}
                            </div>
                          </CommandItem>
                        )
                      })}
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>
                Justification
                <span className="text-muted-foreground font-normal ml-1">(minimum 50 characters)</span>
              </Label>
              <Textarea
                rows={5}
                value={justification}
                onChange={e => setJustification(e.target.value)}
                placeholder="Explain why this journalist deserves this award…"
                className="text-sm resize-none text-justify leading-relaxed"
              />
              <p className={`text-xs ${justification.length >= 50 ? 'text-green-600' : 'text-muted-foreground'}`}>
                {justification.length} / 50 min
              </p>
            </div>
            <Button
              className="w-full bg-[#C8A84B] hover:bg-[#B8983B] text-white"
              disabled={submitting || !nominateJournalistId || justification.trim().length < 50}
              onClick={submitNomination}
            >
              {submitting ? 'Submitting…' : 'Submit Nomination'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* ── Past Winners sheet ── */}
      <Sheet open={pastOpen} onOpenChange={setPastOpen}>
        <SheetContent side="right" className="w-[520px] overflow-y-auto">
          <SheetHeader><SheetTitle>Past Winners</SheetTitle></SheetHeader>
          <div className="mt-4">
            {(winners || []).length === 0 ? (
              <p className="text-justify text-sm text-muted-foreground">No past winners on record.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="pb-2 text-left font-medium text-muted-foreground">Year</th>
                    <th className="pb-2 text-left font-medium text-muted-foreground">Award</th>
                    <th className="pb-2 text-left font-medium text-muted-foreground">Winner</th>
                    <th className="pb-2 text-left font-medium text-muted-foreground">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {(winners || []).map((w: any) => {
                    const outlet = w.journalists?.journalist_affiliations?.find((a: any) => a.is_primary)?.media_outlets?.name
                    return (
                      <tr key={w.id} className="border-b last:border-0">
                        <td className="py-2 text-muted-foreground">{w.award_cycles?.year}</td>
                        <td className="py-2 font-medium">{w.award_categories?.name}</td>
                        <td className="py-2">
                          <div>{w.journalists?.first_name} {w.journalists?.last_name}</div>
                          {outlet && <div className="text-[11px] text-muted-foreground">{outlet}</div>}
                        </td>
                        <td className="py-2 text-muted-foreground whitespace-nowrap">
                          {w.declared_at ? new Date(w.declared_at).toLocaleDateString() : ''}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* ── Create Award Cycle sheet ── */}
      {canEdit && (
        <Sheet open={cycleOpen} onOpenChange={setCycleOpen}>
          <SheetContent side="right" className="w-[400px]">
            <SheetHeader><SheetTitle>Create Award Cycle</SheetTitle></SheetHeader>
            <div className="space-y-4 mt-5">
              <div className="space-y-1.5">
                <Label>Cycle Name</Label>
                <Input
                  value={cycleForm.name}
                  onChange={e => setCycleForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Q2 2026"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Year</Label>
                <Input
                  type="number"
                  value={cycleForm.year}
                  onChange={e => setCycleForm(f => ({ ...f, year: parseInt(e.target.value) || new Date().getFullYear() }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Nominations Open</Label>
                <Input type="date" value={cycleForm.starts_at} onChange={e => setCycleForm(f => ({ ...f, starts_at: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label>Nominations Close</Label>
                <Input type="date" value={cycleForm.ends_at} onChange={e => setCycleForm(f => ({ ...f, ends_at: e.target.value }))} />
              </div>
              <Button
                className="w-full bg-[#C8A84B] hover:bg-[#B8983B] text-white"
                disabled={savingCycle || !cycleForm.name || !cycleForm.starts_at || !cycleForm.ends_at}
                onClick={createCycle}
              >
                <Calendar className="w-4 h-4 mr-2" />
                {savingCycle ? 'Creating…' : 'Create Cycle'}
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* ── Add Category sheet ── */}
      {canEdit && (
        <Sheet open={catOpen} onOpenChange={setCatOpen}>
          <SheetContent side="right" className="w-[440px]">
            <SheetHeader><SheetTitle>Add Award Category</SheetTitle></SheetHeader>
            <div className="space-y-4 mt-5">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input
                  value={catForm.name}
                  onChange={e => setCatForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Best Investigative Reporting"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Description</Label>
                <Textarea
                  rows={3}
                  value={catForm.description}
                  onChange={e => setCatForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Criteria and scope for this award category…"
                  className="resize-none text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Icon</Label>
                  <Select value={catForm.icon_name} onValueChange={v => setCatForm(f => ({ ...f, icon_name: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.keys(ICON_MAP).map(k => (
                        <SelectItem key={k} value={k}>
                          <span className="capitalize">{k}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Coverage Points</Label>
                  <Input
                    type="number"
                    min={0}
                    value={catForm.points}
                    onChange={e => setCatForm(f => ({ ...f, points: parseInt(e.target.value) || 0 }))}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Prize Description <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Input
                  value={catForm.prize_description}
                  onChange={e => setCatForm(f => ({ ...f, prize_description: e.target.value }))}
                  placeholder="e.g. Certificate + $500 prize"
                />
              </div>
              <Button
                className="w-full bg-[#C8A84B] hover:bg-[#B8983B] text-white"
                disabled={savingCat || !catForm.name}
                onClick={createCategory}
              >
                {savingCat && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {savingCat ? 'Creating…' : 'Create Category'}
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* ── Declare Winner sheet ── */}
      <Sheet open={!!winnerNom} onOpenChange={open => { if (!open) setWinnerNom(null) }}>
        <SheetContent side="right" className="w-[400px]">
          <SheetHeader>
            <SheetTitle>Declare Winner</SheetTitle>
          </SheetHeader>
          {winnerNom && (
            <div className="mt-6 space-y-4">
              <div className="p-4 rounded-lg bg-[#C8A84B]/5 border border-[#C8A84B]/20 space-y-2">
                <div className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-[#C8A84B]" />
                  <span className="font-semibold text-sm">
                    {winnerNom.journalists?.first_name} {winnerNom.journalists?.last_name}
                  </span>
                </div>
                <p className="text-justify text-xs text-muted-foreground">
                  Category: <span className="font-medium text-foreground">
                    {(categories || []).find((c: any) => c.id === winnerNom.category_id)?.name ?? ''}
                  </span>
                </p>
                <p className="text-justify text-xs text-muted-foreground">Cycle: {activeCycle?.name}</p>
              </div>
              <p className="text-justify text-sm text-muted-foreground">
                This will record the journalist as winner for this category and cycle. This action cannot be undone.
              </p>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setWinnerNom(null)}>
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-[#C8A84B] hover:bg-[#B8983B] text-white"
                  disabled={declaringWinner}
                  onClick={declareWinner}
                >
                  {declaringWinner && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {declaringWinner ? 'Saving…' : 'Confirm Winner'}
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  )
}
